import React from "react";
import { useParams, Navigate } from "react-router-dom";
import { useI18n, NLink } from "@/i18n/I18nProvider";
import { LEGAL } from "@/i18n/legal";
import { ArrowLeft, ArrowRight } from "lucide-react";

const PageHeader = ({ kicker, title }) => (
  <section className="bg-[#0F1B2D] text-white pt-28 sm:pt-36 pb-16 sm:pb-20 relative">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
      <div className="overline text-[#C4A46A]">{kicker}</div>
      <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight">
        {title}
      </h1>
      <span className="gold-rule mt-8 block" />
    </div>
  </section>
);

const LegalHub = () => {
  const { lang } = useI18n();
  const l = LEGAL[lang].hub;
  return (
    <>
      <PageHeader kicker={l.kicker} title={l.title} />
      <section className="bg-[#F4F0E8]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 sm:py-24">
          <p className="text-[#5E6470] text-lg leading-relaxed max-w-2xl">{l.intro}</p>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-[#0F1B2D]/10">
            {l.items.map((item, i) => (
              <NLink
                key={item.slug}
                to={`/legal/${item.slug}`}
                data-testid={`legal-${item.slug}`}
                className="group bg-[#F4F0E8] p-10 hover:bg-white transition-colors flex flex-col min-h-[200px]"
              >
                <div className="font-display text-[#C4A46A] text-sm">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h2 className="mt-4 font-display text-2xl text-[#0F1B2D] tracking-tight">{item.t}</h2>
                <span className="gold-rule mt-3 block" />
                <p className="mt-4 text-[#5E6470] text-[15px] leading-relaxed flex-1">{item.d}</p>
                <div className="mt-6 inline-flex items-center gap-2 text-[#0F1B2D] text-[11px] tracking-[0.24em] uppercase font-bold group-hover:text-[#A38755] transition-colors">
                  → <ArrowRight size={12} />
                </div>
              </NLink>
            ))}
          </div>
          <p className="mt-12 text-[#5E6470]/70 italic text-sm">{l.intro}</p>
        </div>
      </section>
    </>
  );
};

const LegalDoc = () => {
  const { slug } = useParams();
  const { lang } = useI18n();
  const doc = LEGAL[lang][slug];
  if (!doc) return <Navigate to={`/${lang}/legal`} replace />;

  return (
    <>
      <PageHeader kicker={LEGAL[lang].hub.title} title={doc.title} />
      <section className="bg-[#F4F0E8]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 py-16 sm:py-24">
          <NLink
            to="/legal"
            data-testid="legal-back"
            className="inline-flex items-center gap-2 text-[#0F1B2D] text-[11px] uppercase tracking-[0.24em] font-bold mb-10 hover:text-[#A38755]"
          >
            <ArrowLeft size={14} /> {LEGAL[lang].hub.title}
          </NLink>
          <div className="overline text-[#5E6470]">{doc.updated}</div>
          {doc.intro && (
            <p className="mt-6 text-[#5E6470] text-lg leading-relaxed">{doc.intro}</p>
          )}
          <div className="mt-12 space-y-10">
            {doc.sections.map((s, i) => (
              <div key={s.h} data-testid={`legal-section-${i}`}>
                <h2 className="font-display text-xl lg:text-2xl text-[#0F1B2D] tracking-tight">{s.h}</h2>
                <span className="gold-rule mt-3 block" />
                {s.p && <p className="mt-4 text-[#5E6470] leading-relaxed whitespace-pre-line">{s.p}</p>}
                {s.list && (
                  <ul className="mt-4 space-y-2 text-[#5E6470] leading-relaxed">
                    {s.list.map((li) => (
                      <li key={li} className="pl-5 relative before:content-['·'] before:text-[#C4A46A] before:absolute before:left-0 before:font-bold">{li}</li>
                    ))}
                  </ul>
                )}
                {s.post && <p className="mt-4 text-[#5E6470] leading-relaxed">{s.post}</p>}
              </div>
            ))}
          </div>
          <p className="mt-16 text-[#5E6470]/70 italic text-sm">
            {doc.updated}
          </p>
        </div>
      </section>
    </>
  );
};

export { LegalHub, LegalDoc };
